import { signalTypeMap } from "app/pages/sales-intelligence/domain/signals/types/signal";
import type {
    Signal,
    SignalFilters,
    SignalSource,
    SignalType,
} from "app/pages/sales-intelligence/domain/signals/types/signal";
import type SignalComparable from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalComparable";
import type SignalsGroupedByType from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalsGroupedByType";
import type { FilterService } from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterService";
import type { SignalsValidationService } from "app/pages/sales-intelligence/sub-modules/signals/models/services/SignalsValidationService";

type Deps = { filterService: FilterService };

const createSignalsValidationService = ({ filterService }: Deps): SignalsValidationService => {
    return {
        getSignalsOfType<F extends SignalFilters>(
            { signalType, signalSubType }: SignalComparable<F>,
            signalsGroupedByType: Readonly<SignalsGroupedByType>,
        ): readonly Signal<F>[] {
            const shouldGetSignalsByType = typeof signalSubType === "undefined" || signalType === signalTypeMap.news;

            if (shouldGetSignalsByType) {
                return signalsGroupedByType[signalType];
            }

            return signalsGroupedByType[signalType].filter((signal) => signal.signalSubType === signalSubType);
        },

        checkIsSignalDuplicateBy<F extends SignalFilters>(
            signalType: SignalType,
            signalSource: SignalSource,
        ): (filters: Partial<F>, signalsOfType: ReadonlyArray<Signal<F>>) => boolean {
            const filtersPredicate = filterService.getFiltersEqualityPredicate(signalType);

            return function isSignalDuplicate(filters, signalsOfType) {
                return signalsOfType.some(
                    (signal) => signal.signalSource === signalSource && filtersPredicate(filters, signal.filters),
                );
            };
        },
    };
};

export default createSignalsValidationService;
